<script setup lang="ts">
import { useCustomers } from '@/composables/core/customers'
import { NotifyError } from '@/helpers/core/notifications'
import { resetForm } from '@/helpers/ep/form'
import { getSingleCustomer } from '@/services/core.service'
import type { ICreateAddressForm, ICreateContactForm } from '@/types/core/forms'
import type { CustomerForm } from '@/types/rc/forms'
import { type FormInstance, type FormRules } from 'element-plus'
import { vMaska } from 'maska/vue'
import { computed, reactive, ref, watchEffect } from 'vue'

const open = defineModel<boolean>()
const props = defineProps<{ customerId?: number }>()
const activeCollpaseItems = ref<string[]>([])
const customerDetailsLoading = ref<boolean>(false)
const emit = defineEmits(['closed'])

/**
 * Fetch customer details on customer ID prop change.
 */
watchEffect(async () => {
  if (open.value && props.customerId) {
    customerDetailsLoading.value = true
    try {
      const customerData = await getSingleCustomer(props.customerId)
      const { billing_contact, operations_contact, billing_address, corporate_address, ...customer } = customerData
      customer.phone = customer.phone ?? ''
      customer.company_name = customer.company_name ?? ''
      Object.assign(customerForm, customer)
      Object.assign(billingAddressForm, billing_address)
      Object.assign(billingContactForm, billing_contact)
      Object.assign(corporateAddressForm, corporate_address)
      Object.assign(operationsContactForm, operations_contact)
    } catch (error) {
      NotifyError(error as Error, 'Failed to load customer details.')
      open.value = false
    } finally {
      customerDetailsLoading.value = false
    }
  }
})

const billingContactFormRef = ref<FormInstance>()
const billingContactForm = reactive<ICreateContactForm>({
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
})

const billingAddressFormRef = ref<FormInstance>()
const billingAddressForm = reactive<ICreateAddressForm>({
  address_1: '',
  address_2: '',
  city: '',
  state: '',
  zip: '',
  country: '',
})

const operationsContactFormRef = ref<FormInstance>()
const operationsContactForm = reactive<ICreateContactForm>({
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
})

const corporateAddressFormRef = ref<FormInstance>()
const corporateAddressForm = reactive<ICreateAddressForm>({
  address_1: '',
  address_2: '',
  city: '',
  state: '',
  zip: '',
  country: '',
})

const customerFormRef = ref<FormInstance>()
const customerForm = reactive<CustomerForm>({
  alias: '',
  email: '',
  phone: '',
  first_name: '',
  last_name: '',
  company_name: '',
})

const rules = reactive<FormRules<CustomerForm>>({
  alias: [
    { required: true, message: 'Alias is required.', trigger: 'blur' },
    { min: 4, message: 'Alias must be 4 characters long.', trigger: 'blur' },
    { max: 4, message: 'Alias must be 4 characters long.', trigger: 'blur' },
    {
      pattern: /^[A-Z0-9]+$/,
      message: 'Alias must consist of uppercase letters or numbers.',
      trigger: 'blur',
    },
  ],
  email: [{ type: 'email', required: true, message: 'A valid email is required.', trigger: 'blur' }],
  phone: [
    {
      type: 'pattern',
      pattern: /^\+?\d{2,}$/,
      message: 'Phone number must be numeric.',
      trigger: 'blur',
    },
    {
      type: 'pattern',
      pattern: /^\+/,
      message: 'Phone number must include country code.',
      trigger: 'blur',
    },
  ],
})

const addressFormRules = reactive<FormRules<ICreateAddressForm>>({
  address_1: [{ required: true, message: 'Address 1 is required.', trigger: 'submit' }],
  city: [{ required: true, message: 'City is required.', trigger: 'submit' }],
  state: [{ required: true, message: 'State is required.', trigger: 'submit' }],
  zip: [{ required: true, message: 'Zip is required.', trigger: 'submit' }],
  country: [{ required: true, message: 'Country is required.', trigger: 'submit' }],
})

const contactFormRules = reactive<FormRules<ICreateContactForm>>({
  first_name: [{ required: true, message: 'First Name is required.', trigger: 'submit' }],
  last_name: [{ required: true, message: 'Last Name is required.', trigger: 'submit' }],
  email: [{ type: 'email', required: true, message: 'A valid email is required.', trigger: 'submit' }],
  phone: [
    { required: true, message: 'Phone is required.', trigger: 'submit' },
    {
      type: 'pattern',
      pattern: /^\+?\d{2,}$/,
      message: 'Phone number must be numeric.',
      trigger: 'submit',
    },
    {
      type: 'pattern',
      pattern: /^\+/,
      message: 'Phone number must include country code.',
      trigger: 'submit',
    },
  ],
})

// Mutations
const { createCustomerMutation, updateCustomerMutation } = useCustomers()
const {
  isPending: createIsPending,
  mutateAsync: createAsync,
  isError: createIsError,
  error: createError,
  reset: resetCreate,
} = createCustomerMutation
const {
  isPending: updateIsPending,
  mutateAsync: updateAsync,
  isError: updateIsError,
  error: updateError,
  reset: resetUpdate,
} = updateCustomerMutation

/**
 * Return form loading state (used to disable form).
 */
const formLoading = computed<boolean>(() => {
  return createIsPending.value || updateIsPending.value || customerDetailsLoading.value
})

/**
 * Return form loading text based on action.
 */
const formLoadingText = computed<string>(() => {
  if (createIsPending.value) {
    return 'Creating customer...'
  } else if (updateIsPending.value) {
    return 'Updating customer...'
  } else {
    return 'Loading customer...'
  }
})

const isAnyAddressFieldSet = (addressForm: ICreateAddressForm) => {
  return (
    addressForm.address_1 ||
    addressForm.address_2 ||
    addressForm.city ||
    addressForm.country ||
    addressForm.state ||
    addressForm.zip
  )
}

const isAnyContactFieldSet = (contactForm: ICreateContactForm) => {
  return contactForm.first_name || contactForm.last_name || contactForm.email || contactForm.phone
}

/**
 * Handles form submit.
 * Create or updates a customer, depending on the form mode.
 *
 * @param formEl form element ref.
 */
const submitForm = async (formEl: FormInstance | undefined) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  formEl?.validate(async (valid, fields) => {
    if (!valid) {
      return
    }

    let extraFormErrors: boolean = false

    // Validate Billing Address form
    if (billingAddressFormRef.value && isAnyAddressFieldSet(billingAddressForm)) {
      await billingAddressFormRef.value.validate((valid) => {
        if (!valid) {
          extraFormErrors = true
          activeCollpaseItems.value.push('billingAddressItem')
        }
      })
    }

    // Validate Billing Contact form
    if (billingContactFormRef.value && isAnyContactFieldSet(billingContactForm)) {
      await billingContactFormRef.value.validate((valid) => {
        if (!valid) {
          extraFormErrors = true
          activeCollpaseItems.value.push('billingContactItem')
        }
      })
    }

    // Validate Corporate Address form
    if (corporateAddressFormRef.value && isAnyAddressFieldSet(corporateAddressForm)) {
      await corporateAddressFormRef.value.validate((valid) => {
        if (!valid) {
          extraFormErrors = true
          activeCollpaseItems.value.push('corporateAddressItem')
        }
      })
    }

    // Validate Operations Contact form
    if (operationsContactFormRef.value && isAnyContactFieldSet(operationsContactForm)) {
      await operationsContactFormRef.value.validate((valid) => {
        if (!valid) {
          extraFormErrors = true
          activeCollpaseItems.value.push('operationsContactItem')
        }
      })
    }

    if (extraFormErrors) {
      return
    }

    // If no fields were set on any of the forms, mark the object as null
    if (isAnyAddressFieldSet(billingAddressForm)) customerForm.billing_address = billingAddressForm
    else customerForm.billing_address = null

    if (isAnyContactFieldSet(billingContactForm)) customerForm.billing_contact = billingContactForm
    else customerForm.billing_contact = null

    if (isAnyAddressFieldSet(corporateAddressForm)) customerForm.corporate_address = corporateAddressForm
    else customerForm.corporate_address = null

    if (isAnyContactFieldSet(operationsContactForm)) customerForm.operations_contact = operationsContactForm
    else customerForm.operations_contact = null

    try {
      if (props.customerId) {
        await updateAsync({ customerId: props.customerId, form: customerForm })
      } else {
        await createAsync({ ...customerForm })
      }
      open.value = false
    } catch {
      // handled in UI
    }
  })
}

/**
 * Reset form, reset mutations, emit closed (unsets customer ID prop).
 */
const handleClosed = () => {
  resetForm(customerFormRef.value)
  resetForm(billingAddressFormRef.value)
  resetForm(billingContactFormRef.value)
  resetForm(operationsContactFormRef.value)
  resetForm(corporateAddressFormRef.value)
  resetCreate()
  resetUpdate()
  emit('closed')
}
</script>

<template>
  <el-dialog
    v-model="open"
    :width="600"
    :title="`${customerId ? 'Update' : 'Create'} Customer`"
    :show-close="false"
    @closed="handleClosed"
  >
    <el-form
      ref="customerFormRef"
      v-loading="formLoading"
      :model="customerForm"
      :rules="rules"
      :label-width="160"
      label-position="left"
      hide-required-asterisk
      :disabled="formLoading"
      :element-loading-text="formLoadingText"
    >
      <el-form-item label="Alias" prop="alias" :class="{ invisible: formLoading }">
        <el-input v-model="customerForm.alias" placeholder="ABCD" clearable />
      </el-form-item>
      <el-form-item label="Company Name" prop="company_name" :class="{ invisible: formLoading }">
        <el-input v-model="customerForm.company_name" placeholder="Example, Inc." clearable />
      </el-form-item>
      <el-form-item label="Email" prop="email" :class="{ invisible: formLoading }">
        <el-input
          v-model="customerForm.email"
          type="email"
          required
          placeholder="john.doe@example.com"
          clearable
          :disabled="!!customerId"
        />
      </el-form-item>
      <el-form-item label="Phone" prop="phone" :class="{ invisible: formLoading }">
        <el-input
          v-model="customerForm.phone"
          v-maska
          type="tel"
          data-maska="+9"
          data-maska-tokens="9:[0-9]:repeated"
          placeholder="+0 (000) 000-0000"
          clearable
        />
      </el-form-item>
      <el-form-item label="First Name" prop="first_name" :class="{ invisible: formLoading }">
        <el-input v-model="customerForm.first_name" placeholder="John" clearable />
      </el-form-item>
      <el-form-item label="Last Name" prop="last_name" :class="{ invisible: formLoading }">
        <el-input v-model="customerForm.last_name" placeholder="Doe" clearable />
      </el-form-item>
      <el-collapse v-model="activeCollpaseItems">
        <el-collapse-item title="Billing Address" name="billingAddressItem">
          <el-form
            ref="billingAddressFormRef"
            :model="billingAddressForm"
            :rules="addressFormRules"
            hide-required-asterisk
            label-width="auto"
            label-position="left"
          >
            <el-form-item label="Address 1" prop="address_1">
              <el-input v-model="billingAddressForm.address_1" placeholder="Address 1" clearable />
            </el-form-item>
            <el-form-item label="Address 2" prop="address_2">
              <el-input v-model="billingAddressForm.address_2" placeholder="Address 2" clearable />
            </el-form-item>
            <el-form-item label="City" prop="city">
              <el-input v-model="billingAddressForm.city" placeholder="City" clearable />
            </el-form-item>
            <el-form-item label="State" prop="state">
              <el-input v-model="billingAddressForm.state" placeholder="State" clearable />
            </el-form-item>
            <el-form-item label="Zip" prop="zip">
              <el-input v-model="billingAddressForm.zip" placeholder="Zip" clearable />
            </el-form-item>
            <el-form-item label="Country" prop="country">
              <el-input v-model="billingAddressForm.country" placeholder="Country" clearable />
            </el-form-item>
          </el-form>
        </el-collapse-item>
        <el-collapse-item title="Billing Contact" name="billingContactItem">
          <el-form
            ref="billingContactFormRef"
            :model="billingContactForm"
            :rules="contactFormRules"
            hide-required-asterisk
            label-width="auto"
            label-position="left"
          >
            <el-form-item label="First Name" prop="first_name">
              <el-input v-model="billingContactForm.first_name" placeholder="First Name" clearable />
            </el-form-item>
            <el-form-item label="Last Name" prop="last_name">
              <el-input v-model="billingContactForm.last_name" placeholder="Last Name" clearable />
            </el-form-item>
            <el-form-item label="Phone" prop="phone">
              <el-input
                v-model="billingContactForm.phone"
                v-maska
                type="tel"
                data-maska="+9"
                data-maska-tokens="9:[0-9]:repeated"
                placeholder="+0 (000) 000-0000"
                clearable
              />
            </el-form-item>
            <el-form-item label="Email" prop="email">
              <el-input
                v-model="billingContactForm.email"
                type="email"
                required
                placeholder="john.doe@example.com"
                clearable
              />
            </el-form-item>
          </el-form>
        </el-collapse-item>
        <el-collapse-item title="Corporate Address" name="corporateAddressItem">
          <el-form
            ref="corporateAddressFormRef"
            :model="corporateAddressForm"
            :rules="addressFormRules"
            hide-required-asterisk
            label-width="auto"
            label-position="left"
          >
            <el-form-item label="Address 1" prop="address_1">
              <el-input v-model="corporateAddressForm.address_1" placeholder="Address 1" clearable />
            </el-form-item>
            <el-form-item label="Address 2" prop="address_2">
              <el-input v-model="corporateAddressForm.address_2" placeholder="Address 2" clearable />
            </el-form-item>
            <el-form-item label="City" prop="city">
              <el-input v-model="corporateAddressForm.city" placeholder="City" clearable />
            </el-form-item>
            <el-form-item label="State" prop="state">
              <el-input v-model="corporateAddressForm.state" placeholder="State" clearable />
            </el-form-item>
            <el-form-item label="Zip" prop="zip">
              <el-input v-model="corporateAddressForm.zip" placeholder="Zip" clearable />
            </el-form-item>
            <el-form-item label="Country" prop="country">
              <el-input v-model="corporateAddressForm.country" placeholder="Country" clearable />
            </el-form-item>
          </el-form>
        </el-collapse-item>
        <el-collapse-item title="Operations Contact" name="operationsContactItem">
          <el-form
            ref="operationsContactFormRef"
            :model="operationsContactForm"
            :rules="contactFormRules"
            hide-required-asterisk
            label-width="auto"
            label-position="left"
          >
            <el-form-item label="First Name" prop="first_name">
              <el-input v-model="operationsContactForm.first_name" placeholder="First Name" clearable />
            </el-form-item>
            <el-form-item label="Last Name" prop="last_name">
              <el-input v-model="operationsContactForm.last_name" placeholder="Last Name" clearable />
            </el-form-item>
            <el-form-item label="Phone" prop="phone">
              <el-input
                v-model="operationsContactForm.phone"
                v-maska
                type="tel"
                data-maska="+9"
                data-maska-tokens="9:[0-9]:repeated"
                placeholder="+0 (000) 000-0000"
                clearable
              />
            </el-form-item>
            <el-form-item label="Email" prop="email">
              <el-input
                v-model="operationsContactForm.email"
                type="email"
                required
                placeholder="john.doe@example.com"
                clearable
              />
            </el-form-item>
          </el-form>
        </el-collapse-item>
      </el-collapse>
    </el-form>
    <rl-error-alert v-if="createIsError" :error="createError" />
    <rl-error-alert v-else-if="updateIsError" :error="updateError" />
    <template #footer>
      <el-button @click="open = false">Cancel</el-button>
      <el-button
        type="primary"
        :loading="createIsPending"
        :disabled="formLoading || !open"
        @click="submitForm(customerFormRef)"
        >Submit</el-button
      >
    </template>
  </el-dialog>
</template>
